import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTalentos, getAllTalentosAdmin, createTalento, updateTalento, deleteTalento, getPosts, createPost, updatePost, deletePost, getBrands, createBrand, updateBrand, deleteBrand, seedBrands } from '../services/api';
import { extractIgShortcode } from '../lib/instagram';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, ArrowLeft, Instagram, Upload, Lock, Image, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';
import BackToPanelButton, { BackToPanelLink, redirectToHubIfFromHub } from '../components/BackToPanelButton';
import RosterBonoUploader from '../components/RosterBonoUploader';
import { useIdleLogout } from '../hooks/useIdleLogout';

const AdminPanel = () => {
  const { toast } = useToast();
  const [talentos, setTalentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTalento, setEditingTalento] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Auth state — backed by HttpOnly cookie
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [showRosterBono, setShowRosterBono] = useState(false);
  // 'visible' (default): sólo talentos con visibility === 'public'.
  // 'all': muestra también los ocultos. Toggle en el header del listado.
  const [visibilityFilter, setVisibilityFilter] = useState('visible');
  
  const [formData, setFormData] = useState({
    name: '',
    image: '',
    instagram_url: '',
    tiktok_url: '',
    youtube_url: '',
    vertical: '',
    descripcion: '',
    paises: '',
    edades: '',
    sexo: '',
    instagram_followers: '',
    tiktok_followers: '',
    youtube_subscribers: '',
    pais_residencia: '',
    tipo_campana: '',
    visibility: 'public'
  });

  const fetchTalentos = useCallback(async () => {
    try {
      // Admin sees ALL talents (public + hidden)
      const data = await getAllTalentosAdmin();
      setTalentos(data);
    } catch (err) {
      // Fallback to public list if admin endpoint fails (e.g. session not yet valid)
      try {
        const data = await getTalentos();
        setTalentos(data);
      } catch {
        toast({ title: "Error", description: "No se pudieron cargar los talentos.", variant: "destructive" });
      }
    }
  }, [toast]);

  // Check existing session on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        if (!cancelled && res.ok) setIsAuthenticated(true);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTalentos();
    }
  }, [isAuthenticated, fetchTalentos]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoggingIn(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        setIsAuthenticated(true);
        setPassword('');
      } else {
        const data = await res.json().catch(() => ({}));
        setAuthError(data.detail || 'Contraseña incorrecta');
      }
    } catch {
      setAuthError('Error de red');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }); } catch { /* ignore */ }
    setIsAuthenticated(false);
  };

  // Auto-logout after 30 minutes of inactivity
  useIdleLogout({
    minutes: 30,
    onIdle: (reason) => {
      if (isAuthenticated) {
        setAuthError(reason === 'expired'
          ? 'Tu sesión expiró. Volvé a ingresar.'
          : 'Cerramos la sesión por inactividad (30 min).');
        handleLogout();
      }
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingTalento) {
        await updateTalento(editingTalento.id, formData);
        toast({
          title: "¡Actualizado!",
          description: "El talento ha sido actualizado exitosamente.",
        });
      } else {
        await createTalento(formData);
        toast({
          title: "¡Creado!",
          description: "El talento ha sido agregado exitosamente.",
        });
      }
      
      fetchTalentos();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: "Hubo un problema. Intenta de nuevo.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (talento) => {
    setEditingTalento(talento);
    setFormData({
      name: talento.name,
      image: talento.image,
      instagram_url: talento.instagram_url || '',
      tiktok_url: talento.tiktok_url || '',
      youtube_url: talento.youtube_url || '',
      vertical: talento.vertical || '',
      descripcion: talento.descripcion || '',
      paises: talento.paises || '',
      edades: talento.edades || '',
      sexo: talento.sexo || '',
      instagram_followers: talento.instagram_followers || '',
      tiktok_followers: talento.tiktok_followers || '',
      youtube_subscribers: talento.youtube_subscribers || '',
      pais_residencia: talento.pais_residencia || '',
      tipo_campana: talento.tipo_campana || '',
      visibility: talento.visibility || 'public'
    });
    setImagePreview(talento.image);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este talento?')) {
      return;
    }

    try {
      await deleteTalento(id);
      toast({
        title: "¡Eliminado!",
        description: "El talento ha sido eliminado exitosamente.",
      });
      fetchTalentos();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el talento.",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      image: '',
      instagram_url: '',
      tiktok_url: '',
      youtube_url: '',
      vertical: '',
      descripcion: '',
      paises: '',
      edades: '',
      sexo: '',
      instagram_followers: '',
      tiktok_followers: '',
      youtube_subscribers: '',
      pais_residencia: '',
      tipo_campana: '',
      visibility: 'public'
    });
    setEditingTalento(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Error",
        description: "Solo se permiten imágenes JPG, PNG, WEBP o GIF",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "La imagen es muy grande. Máximo 10MB.",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`/api/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error('Error al subir la imagen');
      }

      const data = await response.json();
      
      setFormData(prev => ({ ...prev, image: data.url }));
      setImagePreview(data.url);
      
      toast({
        title: "¡Imagen subida!",
        description: "La imagen se subió correctamente.",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo subir la imagen. Intenta de nuevo.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  // Login Screen
  if (!isAuthenticated) {
    // Session expired or never authenticated. If user came via /panel,
    // bounce them back to the unified Hub instead of the local login form.
    if (redirectToHubIfFromHub()) return null;
    return (
      <div className="min-h-screen pt-20 bg-black flex items-center justify-center">
        <Card className="w-full max-w-md bg-gray-900 border-gray-800">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-cyan-500" />
            </div>
            <CardTitle className="text-2xl text-white">Panel de Administración</CardTitle>
            <p className="text-gray-400 text-sm mt-2">Ingresá la contraseña para continuar</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                  placeholder="Ingresá la contraseña"
                  autoFocus
                />
                {authError && (
                  <p className="text-red-500 text-sm">{authError}</p>
                )}
              </div>
              <Button type="submit" disabled={loggingIn || !password} className="w-full bg-cyan-500 hover:bg-cyan-600">
                {loggingIn ? 'Verificando...' : 'Ingresar'}
              </Button>
              <Link to="/">
                <Button type="button" variant="ghost" className="w-full text-gray-400 hover:text-white mt-2">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al inicio
                </Button>
              </Link>
              <div className="text-center mt-3">
                <BackToPanelLink />
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Panel de Administración</h1>
            <p className="text-gray-400">Gestiona tu roster de talentos</p>
          </div>
          <div className="flex gap-2">
            <Link to="/talentos">
              <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver a Talentos
              </Button>
            </Link>
            <Button
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
              onClick={() => setShowRosterBono(true)}
              data-testid="admin-open-roster-bono-btn"
              title="Cargar el roster para el cálculo del bono trimestral"
            >
              <span className="mr-2">👥</span>
              Roster Bono
            </Button>
            <BackToPanelButton />
            <Button onClick={handleLogout} variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>

        <RosterBonoUploader
          open={showRosterBono}
          onClose={() => setShowRosterBono(false)}
        />

        {/* Import + Edit dialog (edit-only trigger from talent cards) */}
        <div className="mb-8 flex gap-3 flex-wrap">
          <ImportExcelTalentsButton toast={toast} onImported={fetchTalentos} />
          <BackfillTipoCampanaButton toast={toast} onDone={fetchTalentos} />
          <NormalizeVerticalsButton toast={toast} onDone={fetchTalentos} />
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => resetForm()}
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="add-new-talento-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar Nuevo Talento
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {editingTalento ? 'Editar Talento' : 'Agregar Nuevo Talento'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre del Talento *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-gray-800 border-gray-700"
                    placeholder="Ej: María González"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="image">Foto del Talento *</Label>
                  
                  {/* Image Preview */}
                  {(imagePreview || formData.image) && (
                    <div className="mb-3">
                      <img 
                        src={imagePreview || formData.image} 
                        alt="Preview" 
                        className="w-32 h-32 object-cover rounded-lg border border-gray-700"
                      />
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {uploading ? (
                        <>Subiendo...</>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Subir Foto
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Or use URL */}
                  <div className="relative my-3">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-700" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-gray-900 px-2 text-gray-500">o usar URL</span>
                    </div>
                  </div>
                  
                  <Input
                    id="image"
                    value={formData.image}
                    onChange={(e) => {
                      setFormData({ ...formData, image: e.target.value });
                      setImagePreview(e.target.value);
                    }}
                    className="bg-gray-800 border-gray-700"
                    placeholder="https://ejemplo.com/foto.jpg"
                  />
                  <p className="text-xs text-gray-500">Subí una foto JPG/PNG o pegá una URL de imagen</p>
                </div>
                
                <div className="border-t border-gray-700 pt-4 mt-4">
                  <h4 className="text-white font-semibold mb-3 flex items-center">
                    <Instagram className="w-4 h-4 mr-2 text-pink-500" />
                    Redes Sociales (URLs completas)
                  </h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="instagram" className="flex items-center">
                        <Instagram className="w-4 h-4 mr-2" />
                        Instagram URL
                      </Label>
                      <Input
                        id="instagram"
                        value={formData.instagram_url}
                        onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                        className="bg-gray-800 border-gray-700"
                        placeholder="https://instagram.com/nombreusuario"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tiktok" className="flex items-center">
                        <span className="mr-2">🎵</span>
                        TikTok URL
                      </Label>
                      <Input
                        id="tiktok"
                        value={formData.tiktok_url}
                        onChange={(e) => setFormData({ ...formData, tiktok_url: e.target.value })}
                        className="bg-gray-800 border-gray-700"
                        placeholder="https://tiktok.com/@nombreusuario"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="youtube" className="flex items-center">
                        <span className="mr-2">▶️</span>
                        YouTube URL
                      </Label>
                      <Input
                        id="youtube"
                        value={formData.youtube_url}
                        onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                        className="bg-gray-800 border-gray-700"
                        placeholder="https://youtube.com/@nombrecanal"
                        data-testid="admin-youtube-url-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <div className="space-y-2">
                        <Label htmlFor="ig_followers">Seguidores Instagram</Label>
                        <Input id="ig_followers" value={formData.instagram_followers} onChange={(e) => setFormData({ ...formData, instagram_followers: e.target.value })} className="bg-gray-800 border-gray-700" placeholder="Ej: 1.2M" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tk_followers">Seguidores TikTok</Label>
                        <Input id="tk_followers" value={formData.tiktok_followers} onChange={(e) => setFormData({ ...formData, tiktok_followers: e.target.value })} className="bg-gray-800 border-gray-700" placeholder="Ej: 850K" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="yt_subs">Suscriptores YouTube</Label>
                      <Input id="yt_subs" value={formData.youtube_subscribers} onChange={(e) => setFormData({ ...formData, youtube_subscribers: e.target.value })} className="bg-gray-800 border-gray-700" placeholder="Ej: 250K" />
                    </div>
                  </div>
                </div>

                <div className="border-t border-orange-800/40 pt-4 mt-4">
                  <h4 className="text-orange-400 font-semibold mb-1">Datos internos <span className="text-xs text-gray-400 font-normal">(no se muestran en el site público — sólo admin y comercial)</span></h4>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="space-y-2">
                      <Label htmlFor="pais_residencia">País de residencia</Label>
                      <Input id="pais_residencia" value={formData.pais_residencia} onChange={(e) => setFormData({ ...formData, pais_residencia: e.target.value })} className="bg-gray-800 border-gray-700" placeholder="Ej: Argentina" data-testid="edit-pais-residencia" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tipo_campana">Tipo de campaña</Label>
                      <Input id="tipo_campana" value={formData.tipo_campana} onChange={(e) => setFormData({ ...formData, tipo_campana: e.target.value })} className="bg-gray-800 border-gray-700" placeholder="Ej: Branding, Performance, Awareness" data-testid="edit-tipo-campana" />
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-4 mt-4">
                  <h4 className="text-white font-semibold mb-3">Perfil / Ficha</h4>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="vertical">Verticales</Label>
                      <Input id="vertical" value={formData.vertical} onChange={(e) => setFormData({ ...formData, vertical: e.target.value })} className="bg-gray-800 border-gray-700" placeholder="Ej: Humor, Lifestyle, Beauty" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="descripcion">Descripción</Label>
                      <textarea id="descripcion" value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm text-white resize-none" rows={3} placeholder="Breve descripción del talento..." />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="paises">Países</Label>
                        <Input id="paises" value={formData.paises} onChange={(e) => setFormData({ ...formData, paises: e.target.value })} className="bg-gray-800 border-gray-700" placeholder="Argentina, México, Chile" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sexo">Audiencia (Género)</Label>
                        <Input id="sexo" value={formData.sexo} onChange={(e) => setFormData({ ...formData, sexo: e.target.value })} className="bg-gray-800 border-gray-700" placeholder="Mujeres: 80%, Hombres: 20%" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edades">Edades</Label>
                      <Input id="edades" value={formData.edades} onChange={(e) => setFormData({ ...formData, edades: e.target.value })} className="bg-gray-800 border-gray-700" placeholder="18-24 (45%), 25-34 (32%)" />
                    </div>
                    <div className="space-y-2 pt-2 border-t border-gray-800">
                      <Label htmlFor="visibility" className="flex items-center gap-2">
                        Visibilidad en la web
                      </Label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, visibility: 'public' })}
                          className="flex-1 px-3 py-2 rounded-md text-sm font-semibold transition-all border"
                          style={{
                            backgroundColor: formData.visibility === 'public' ? '#1B6B3A' : 'transparent',
                            borderColor: formData.visibility === 'public' ? '#1B6B3A' : '#374151',
                            color: formData.visibility === 'public' ? 'white' : '#9CA3AF',
                          }}
                          data-testid="admin-visibility-public"
                        >
                          🟢 Público (visible en el roster)
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, visibility: 'hidden' })}
                          className="flex-1 px-3 py-2 rounded-md text-sm font-semibold transition-all border"
                          style={{
                            backgroundColor: formData.visibility === 'hidden' ? '#7C2D12' : 'transparent',
                            borderColor: formData.visibility === 'hidden' ? '#7C2D12' : '#374151',
                            color: formData.visibility === 'hidden' ? 'white' : '#9CA3AF',
                          }}
                          data-testid="admin-visibility-hidden"
                        >
                          🔒 Oculto (solo comercial)
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="flex-1 bg-cyan-500 hover:bg-cyan-600"
                  >
                    {loading ? 'Guardando...' : editingTalento ? 'Actualizar' : 'Agregar'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={handleDialogClose}
                    className="flex-1 border-gray-700"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Talentos Grid */}
        {(() => {
          const visibleCount = talentos.filter((t) => (t.visibility || 'public') !== 'hidden').length;
          const hiddenCount = talentos.length - visibleCount;
          const filtered = visibilityFilter === 'all'
            ? talentos
            : talentos.filter((t) => (t.visibility || 'public') !== 'hidden');
          return (
            <>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="text-sm text-gray-400">
                  Mostrando <span className="text-white font-semibold">{filtered.length}</span> talentos
                  {visibilityFilter === 'visible' && hiddenCount > 0 && (
                    <span className="text-gray-500"> · {hiddenCount} oculto{hiddenCount === 1 ? '' : 's'} sin mostrar</span>
                  )}
                </div>
                <div className="inline-flex rounded-full border border-gray-700 overflow-hidden text-xs font-semibold" data-testid="admin-visibility-toggle">
                  <button
                    type="button"
                    onClick={() => setVisibilityFilter('visible')}
                    className={`px-4 py-2 transition-colors ${visibilityFilter === 'visible' ? 'bg-emerald-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}
                    data-testid="admin-visibility-filter-visible"
                  >
                    Visibles ({visibleCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => setVisibilityFilter('all')}
                    className={`px-4 py-2 transition-colors border-l border-gray-700 ${visibilityFilter === 'all' ? 'bg-cyan-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}
                    data-testid="admin-visibility-filter-all"
                  >
                    Todos ({talentos.length})
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((talento) => (
            <Card key={talento.id} className="bg-gray-900 border-gray-800 overflow-hidden">
              <div className="relative h-64">
                <img
                  src={talento.image}
                  alt={talento.name}
                  className="w-full h-full object-cover grayscale"
                />
                {talento.visibility === 'hidden' && (
                  <span className="absolute top-2 left-2 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-lg" style={{ backgroundColor: '#7C2D12', color: '#FED7AA' }} data-testid={`admin-badge-hidden-${talento.id}`}>
                    🔒 Oculto
                  </span>
                )}
              </div>
              <CardHeader>
                <CardTitle className="text-white">{talento.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400 flex items-center">
                      <Instagram size={16} className="mr-2" />
                      Instagram:
                    </span>
                    <span className={talento.instagram_url ? "text-green-400" : "text-gray-600"}>
                      {talento.instagram_url ? "✓ Configurado" : "Sin configurar"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400 flex items-center">
                      🎵 TikTok:
                    </span>
                    <span className={talento.tiktok_url ? "text-green-400" : "text-gray-600"}>
                      {talento.tiktok_url ? "✓ Configurado" : "Sin configurar"}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEdit(talento)}
                    size="sm"
                    variant="outline"
                    className="flex-1 border-cyan-500 text-cyan-500 hover:bg-cyan-500 hover:text-white"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    onClick={() => handleDelete(talento.id)}
                    size="sm"
                    variant="outline"
                    className="flex-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Eliminar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
              </div>
            </>
          );
        })()}

        {talentos.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-400 text-xl">No hay talentos aún. ¡Agrega el primero!</p>
          </div>
        )}

        {/* Instagram Posts Management */}
        <PostsManager toast={toast} />

        {/* Brands Management */}
        <BrandsManager toast={toast} />
      </div>
    </div>
  );
};

// Instagram Posts Manager Component
const PostsManager = ({ toast }) => {
  const [posts, setPostsList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newPostUrl, setNewPostUrl] = useState('');
  const fileInputRef = useRef(null);

  const fetchPostsData = useCallback(async () => {
    try {
      const data = await getPosts();
      setPostsList(data);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    }
  }, []);

  useEffect(() => {
    fetchPostsData();
  }, [fetchPostsData]);

  const handleAddByUrlOnly = async () => {
    if (!newPostUrl.trim()) {
      toast({ title: "Falta la URL", description: "Pegá el link del post de Instagram.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      await createPost({
        image: '',
        instagram_url: newPostUrl.trim(),
        order: posts.length,
      });
      setNewPostUrl('');
      toast({ title: "Post agregado", description: "Se mostrará embebido desde Instagram." });
      fetchPostsData();
    } catch (err) {
      toast({ title: "Error", description: "No se pudo agregar el post.", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadPost = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/upload', { method: 'POST', credentials: 'include', body: formData });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();

      await createPost({
        image: data.url,
        instagram_url: newPostUrl || '',
        order: posts.length,
      });

      setNewPostUrl('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast({ title: "Post agregado", description: "El post de Instagram se agregó correctamente." });
      fetchPostsData();
    } catch (err) {
      toast({ title: "Error", description: "No se pudo subir el post.", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('¿Eliminar este post?')) return;
    try {
      await deletePost(id);
      toast({ title: "Post eliminado" });
      fetchPostsData();
    } catch (err) {
      toast({ title: "Error", description: "No se pudo eliminar.", variant: "destructive" });
    }
  };

  const handleUpdateUrl = async (id, url) => {
    try {
      await updatePost(id, { instagram_url: url });
      toast({ title: "URL actualizada" });
      fetchPostsData();
    } catch (err) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  return (
    <div className="mt-16 border-t border-gray-200 pt-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6" data-testid="posts-section-title">
        Publicaciones de Instagram
      </h2>

      {/* Add new post */}
      <Card className="mb-6 border-gray-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <Label className="text-gray-700 text-sm">URL del post en Instagram</Label>
              <Input
                placeholder="https://www.instagram.com/p/..."
                value={newPostUrl}
                onChange={(e) => setNewPostUrl(e.target.value)}
                className="mt-1"
                data-testid="post-instagram-url-input"
              />
            </div>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*,video/mp4,video/mov,video/webm"
                onChange={handleUploadPost}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="upload-post-button"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Subiendo...' : 'Subir imagen del post'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Posts grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {posts.map((post) => (
          <Card key={post.id} className="overflow-hidden border-gray-200">
            <div className="aspect-square overflow-hidden bg-gray-100">
              {/\.(mp4|mov|webm)$/i.test(post.image) ? (
                <video src={post.image} className="w-full h-full object-cover" autoPlay loop muted playsInline />
              ) : (
                <img src={post.image} alt="Post" className="w-full h-full object-cover" />
              )}
            </div>
            <CardContent className="p-3 space-y-2">
              <Input
                defaultValue={post.instagram_url}
                placeholder="URL de Instagram"
                onBlur={(e) => {
                  if (e.target.value !== post.instagram_url) {
                    handleUpdateUrl(post.id, e.target.value);
                  }
                }}
                className="text-xs"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDeletePost(post.id)}
                className="w-full border-red-400 text-red-500 hover:bg-red-500 hover:text-white text-xs"
                data-testid={`delete-post-${post.id}`}
              >
                <Trash2 className="w-3 h-3 mr-1" /> Eliminar
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {posts.length === 0 && (
        <p className="text-center text-gray-400 py-8">No hay posts. Subí el primero.</p>
      )}
    </div>
  );
};

// Brands Manager Component
const BrandsManager = ({ toast }) => {
  const [brands, setBrandsList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    scale: 1.2,
  });
  const [logoPreview, setLogoPreview] = useState(null);

  const fetchBrandsData = useCallback(async () => {
    try {
      const data = await getBrands();
      setBrandsList(data);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  }, []);

  useEffect(() => {
    fetchBrandsData();
  }, [fetchBrandsData]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const result = await seedBrands();
      toast({ title: result.seeded ? "Marcas cargadas" : "Info", description: result.message });
      if (result.seeded) fetchBrandsData();
    } catch {
      toast({ title: "Error", description: "No se pudieron cargar las marcas.", variant: "destructive" });
    } finally {
      setSeeding(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/upload', { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setFormData(prev => ({ ...prev, logo: data.url }));
      setLogoPreview(data.url);
      toast({ title: "Logo subido" });
    } catch {
      toast({ title: "Error", description: "No se pudo subir el logo.", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', logo: '', scale: 1.2 });
    setEditingBrand(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBrand) {
        await updateBrand(editingBrand.id, formData);
        toast({ title: "Marca actualizada" });
      } else {
        await createBrand({ ...formData, order: brands.length });
        toast({ title: "Marca creada" });
      }
      fetchBrandsData();
      setIsDialogOpen(false);
      resetForm();
    } catch {
      toast({ title: "Error", description: "No se pudo guardar la marca.", variant: "destructive" });
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({ name: brand.name, logo: brand.logo, scale: brand.scale || 1.2 });
    setLogoPreview(brand.logo);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta marca?')) return;
    try {
      await deleteBrand(id);
      toast({ title: "Marca eliminada" });
      fetchBrandsData();
    } catch {
      toast({ title: "Error", description: "No se pudo eliminar.", variant: "destructive" });
    }
  };

  return (
    <div className="mt-16 border-t border-gray-200 pt-8" data-testid="brands-manager">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-800" data-testid="brands-section-title">
          Marcas que confian en nosotros
        </h2>
        <div className="flex gap-2">
          {brands.length === 0 && (
            <Button
              onClick={handleSeed}
              disabled={seeding}
              variant="outline"
              className="border-green-500 text-green-600 hover:bg-green-500 hover:text-white"
              data-testid="seed-brands-button"
            >
              {seeding ? 'Cargando...' : 'Cargar marcas iniciales'}
            </Button>
          )}
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button
                onClick={() => resetForm()}
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="add-brand-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar Marca
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl">
                  {editingBrand ? 'Editar Marca' : 'Agregar Nueva Marca'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="brand-name">Nombre de la Marca *</Label>
                  <Input
                    id="brand-name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    placeholder="Ej: Nike"
                    data-testid="brand-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Logo de la Marca *</Label>
                  {logoPreview && (
                    <div className="mb-3 p-4 bg-gray-100 rounded-lg flex items-center justify-center">
                      <img src={logoPreview} alt="Preview" className="h-16 w-auto object-contain" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      variant="outline"
                      data-testid="upload-brand-logo-button"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? 'Subiendo...' : 'Subir Logo'}
                    </Button>
                  </div>
                  <div className="relative my-3">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-200" /></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-gray-400">o usar URL</span></div>
                  </div>
                  <Input
                    value={formData.logo}
                    onChange={(e) => { setFormData(prev => ({ ...prev, logo: e.target.value })); setLogoPreview(e.target.value); }}
                    placeholder="https://ejemplo.com/logo.png"
                    data-testid="brand-logo-url-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="brand-scale">Escala del logo (tamaño relativo)</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="brand-scale"
                      type="number"
                      step="0.1"
                      min="0.3"
                      max="4"
                      value={formData.scale}
                      onChange={(e) => setFormData(prev => ({ ...prev, scale: parseFloat(e.target.value) || 1.2 }))}
                      className="w-24"
                      data-testid="brand-scale-input"
                    />
                    <span className="text-sm text-gray-500">1.0 = normal, 2.0 = doble</span>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="save-brand-button">
                    {editingBrand ? 'Actualizar' : 'Agregar'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="flex-1">
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Brands grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {brands.map((brand) => (
          <Card key={brand.id} className="overflow-hidden border-gray-200" data-testid={`brand-card-${brand.id}`}>
            <div className="aspect-[3/2] bg-gray-50 flex items-center justify-center p-4">
              <img src={brand.logo} alt={brand.name} className="max-h-full max-w-full object-contain" />
            </div>
            <CardContent className="p-3 space-y-2">
              <p className="text-sm font-medium text-gray-800 truncate text-center">{brand.name}</p>
              <p className="text-xs text-gray-400 text-center">Escala: {brand.scale || 1.2}</p>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(brand)}
                  className="flex-1 border-cyan-400 text-cyan-600 hover:bg-cyan-500 hover:text-white text-xs"
                  data-testid={`edit-brand-${brand.id}`}
                >
                  <Edit className="w-3 h-3 mr-1" /> Editar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(brand.id)}
                  className="flex-1 border-red-400 text-red-500 hover:bg-red-500 hover:text-white text-xs"
                  data-testid={`delete-brand-${brand.id}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {brands.length === 0 && (
        <div className="text-center py-8">
          <Image className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400">No hay marcas. Usa "Cargar marcas iniciales" para importar las existentes o agrega una nueva.</p>
        </div>
      )}
    </div>
  );
};



// Seed Profiles Button - loads all Excel data directly
const SeedProfilesButton = ({ toast, onImported }) => {
  const [seeding, setSeeding] = useState(false);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await fetch('/api/talentos/seed-profiles', { method: 'POST', credentials: 'include' });
      const data = await res.json();
      toast({
        title: "Perfiles cargados",
        description: `${data.updated} talentos actualizados.${data.not_found?.length > 0 ? ` No encontrados: ${data.not_found.join(', ')}` : ''}`,
      });
      if (onImported) onImported();
    } catch (err) {
      console.error('Seed error:', err);
      toast({ title: "Error", description: "No se pudieron cargar los perfiles.", variant: "destructive" });
    } finally {
      setSeeding(false);
    }
  };

  return (
    <Button
      onClick={handleSeed}
      disabled={seeding}
      variant="outline"
      className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
      data-testid="seed-profiles-button"
    >
      {seeding ? 'Cargando...' : 'Cargar Perfiles (Excel)'}
    </Button>
  );
};


// Import Profiles Button Component
const ImportProfilesButton = ({ toast, onImported }) => {
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        toast({ title: "Error", description: "El archivo está vacío o no tiene datos.", variant: "destructive" });
        return;
      }

      // Parse CSV - first line is header
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const profiles = [];

      for (let i = 1; i < lines.length; i++) {
        // Handle commas inside quotes
        const row = [];
        let current = '';
        let inQuotes = false;
        for (const char of lines[i]) {
          if (char === '"') { inQuotes = !inQuotes; }
          else if (char === ',' && !inQuotes) { row.push(current.trim()); current = ''; }
          else { current += char; }
        }
        row.push(current.trim());

        const profile = {};
        headers.forEach((h, idx) => {
          if (h.includes('nombre') || h === 'name') profile.name = row[idx] || '';
          else if (h.includes('vertical')) profile.vertical = row[idx] || '';
          else if (h.includes('descripcion') || h.includes('descripción')) profile.descripcion = row[idx] || '';
          else if (h.includes('pais') || h.includes('país')) profile.paises = row[idx] || '';
          else if (h.includes('edad')) profile.edades = row[idx] || '';
          else if (h.includes('sexo') || h.includes('genero') || h.includes('género') || h.includes('audiencia')) profile.sexo = row[idx] || '';
        });
        if (profile.name) profiles.push(profile);
      }

      if (profiles.length === 0) {
        toast({ title: "Error", description: "No se encontraron perfiles en el archivo.", variant: "destructive" });
        return;
      }

      const res = await fetch('/api/talentos/import-profiles', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profiles),
      });
      const data = await res.json();

      toast({
        title: "Importación completada",
        description: `${data.updated} talentos actualizados.${data.not_found?.length > 0 ? ` No encontrados: ${data.not_found.join(', ')}` : ''}`,
      });
      if (onImported) onImported();
    } catch (err) {
      console.error('Import error:', err);
      toast({ title: "Error", description: "No se pudo importar el archivo.", variant: "destructive" });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <>
      <input type="file" ref={fileRef} onChange={handleImport} accept=".csv,.txt" className="hidden" />
      <Button
        onClick={() => fileRef.current?.click()}
        disabled={importing}
        variant="outline"
        className="border-green-500 text-green-400 hover:bg-green-500 hover:text-white"
        data-testid="import-profiles-button"
      >
        <Upload className="w-4 h-4 mr-2" />
        {importing ? 'Importando...' : 'Importar Perfiles (CSV)'}
      </Button>
    </>
  );
};


// ============ Import Excel Talents (bulk hidden import w/ column mapping) ============
const IMPORT_FIELDS = [
  { key: 'name', label: 'Nombre', required: true },
  { key: 'instagram_url', label: 'Instagram URL', required: true },
  { key: 'instagram_followers', label: 'Instagram Foll.', required: false },
  { key: 'tiktok_url', label: 'TikTok URL', required: false },
  { key: 'tiktok_followers', label: 'TikTok Foll.', required: false },
  { key: 'youtube_url', label: 'YouTube URL', required: false },
  { key: 'youtube_subscribers', label: 'YouTube Subs.', required: false },
  { key: 'vertical', label: 'Vertical', required: false },
  { key: 'pais_residencia', label: 'País de residencia', required: false },
  { key: 'tipo_campana', label: 'Tipo de campaña', required: false },
];
const DEFAULT_MAPPING = {
  name: 'A',
  instagram_url: 'B',
  instagram_followers: 'C',
  tiktok_url: 'G',
  tiktok_followers: 'H',
  youtube_url: 'N',
  youtube_subscribers: 'O',
  vertical: '',
  pais_residencia: '',
  tipo_campana: '',
};
const STORAGE_KEY = 'talengers_import_excel_mapping_v1';

const ImportExcelTalentsButton = ({ toast, onImported }) => {
  const [open, setOpen] = useState(false);
  const [fileB64, setFileB64] = useState('');
  const [fileName, setFileName] = useState('');
  const [startRow, setStartRow] = useState(3);
  const [mapping, setMapping] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      return saved && typeof saved === 'object' ? { ...DEFAULT_MAPPING, ...saved } : DEFAULT_MAPPING;
    } catch { return DEFAULT_MAPPING; }
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [sheets, setSheets] = useState([]);      // hojas disponibles del xlsx
  const [sheetName, setSheetName] = useState(''); // hoja elegida (vacío = primera/activa)
  const [headerRows, setHeaderRows] = useState([]); // primeras N filas de la hoja (para auto-detect)
  // Defaults por hoja: se aplican a las filas que NO traen valor propio en su columna.
  // Ej: hoja "UGC CREATORS" sin columna TIPO CAMPAÑA → default { tipo_campana: 'UGC' }
  //     hace que todos los importados queden con tipo_campana="UGC" y aparezcan al filtrar.
  const [defaults, setDefaults] = useState({ vertical: '', pais_residencia: '', tipo_campana: '' });
  const fileRef = useRef(null);

  const idxToLetter = (idx) => {
    let n = idx + 1, s = '';
    while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
    return s;
  };

  const fetchHeaders = async (b64, sn, dataStartRow) => {
    try {
      // Pedir SOLO las filas ANTES de la fila de datos (por eso startRow - 1).
      // Si el user configuró startRow=3, esto trae 2 filas de header. Nunca menos que 1.
      const rowsNeeded = Math.max(1, Number(dataStartRow || startRow || 3) - 1);
      const res = await fetch('/api/talentos/import-excel/headers', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_base64: b64, sheet_name: sn || null, rows: rowsNeeded }),
      });
      const data = await res.json();
      if (res.ok && Array.isArray(data.rows)) setHeaderRows(data.rows);
    } catch (_e) { setHeaderRows([]); }
  };

  // Heurística de auto-detección: mira la ÚLTIMA fila de headers (sub-header) para nombre/rubro/link/foll,
  // y usa las filas superiores como CONTEXTO de sección (INSTAGRAM/TIKTOK/YOUTUBE/TALENGERS) para
  // desambiguar los LINK y FOLL. Las secciones se PROPAGAN a la derecha hasta encontrar otra sección.
  const autoDetect = () => {
    if (headerRows.length === 0) {
      toast({ title: 'Sin headers para analizar', description: 'Elegí un archivo y una hoja primero', variant: 'destructive' });
      return;
    }
    const maxCol = Math.max(...headerRows.map(r => r.length));
    const subHeader = headerRows[headerRows.length - 1] || [];
    const topRows = headerRows.slice(0, -1);

    // Propagación de sección desde las filas superiores.
    const section = new Array(maxCol).fill('');
    let currentSection = '';
    const KNOWN_SECTIONS = ['INSTAGRAM', 'TIKTOK', 'YOUTUBE', 'TWITCH', 'TALENGERS'];
    for (let c = 0; c < maxCol; c++) {
      // Si alguna fila superior en esta columna dice una sección conocida, actualizarla.
      for (const row of topRows) {
        const cell = (row[c] || '').toString().trim().toUpperCase();
        if (KNOWN_SECTIONS.includes(cell)) { currentSection = cell; break; }
      }
      section[c] = currentSection;
    }

    const next = { ...DEFAULT_MAPPING };
    Object.keys(next).forEach(k => { next[k] = ''; });

    for (let c = 0; c < maxCol; c++) {
      const sub = (subHeader[c] || '').toString().trim().toUpperCase();
      const sec = section[c];
      const letter = idxToLetter(c);
      if (!sub) continue;

      // NAME: "NOMBRE" en subheader, o "TALENGERS" en subheader (sheets sin fila de secciones)
      if (!next.name && (sub === 'NOMBRE' || sub === 'TALENGERS')) next.name = letter;
      // VERTICAL: RUBRO / VERTICAL / CATEGORÍA
      if (!next.vertical && (sub.includes('RUBRO') || sub.includes('VERTICAL') || sub.includes('CATEGOR'))) next.vertical = letter;
      // TIPO CAMPAÑA
      if (!next.tipo_campana && sub.includes('TIPO') && sub.includes('CAMPA')) next.tipo_campana = letter;
      // PAÍS RESIDENCIA / UBICACIÓN
      if (!next.pais_residencia && (sub.includes('UBICACI') || sub === 'PAÍS' || sub === 'PAIS' || sub.includes('COUNTRY') || sub.includes('PAÍSES') || sub.includes('PAISES'))) next.pais_residencia = letter;

      // LINK columns (sub === LINK exacto, con context de sección)
      if (sub === 'LINK') {
        if (sec === 'INSTAGRAM' && !next.instagram_url) next.instagram_url = letter;
        else if (sec === 'TIKTOK' && !next.tiktok_url) next.tiktok_url = letter;
        else if (sec === 'YOUTUBE' && !next.youtube_url) next.youtube_url = letter;
      }
      // FOLL (Instagram/TikTok)
      if (sub === 'FOLL') {
        if (sec === 'INSTAGRAM' && !next.instagram_followers) next.instagram_followers = letter;
        else if (sec === 'TIKTOK' && !next.tiktok_followers) next.tiktok_followers = letter;
      }
      // Suscriptores (YouTube)
      if ((sub.includes('SUSCRIPTORES') || sub.includes('SUBS')) && sec === 'YOUTUBE' && !next.youtube_subscribers) {
        next.youtube_subscribers = letter;
      }
    }

    // Fallback: si topRows está vacío (sheets con una sola fila de header) o sección TALENGERS
    // se detectó pero no encontramos NOMBRE en subheader, intentar detectar TALENGERS en la primera fila.
    if (!next.name && topRows.length === 0) {
      for (let c = 0; c < maxCol; c++) {
        const cell = (headerRows[0][c] || '').toString().trim().toUpperCase();
        if (cell === 'TALENGERS' || cell === 'NOMBRE') { next.name = idxToLetter(c); break; }
      }
    }

    if (!next.name || !next.instagram_url) {
      toast({
        title: 'Detección parcial',
        description: `No pude encontrar ${!next.name ? '"Nombre"' : ''}${!next.name && !next.instagram_url ? ' y ' : ''}${!next.instagram_url ? '"Instagram URL"' : ''}. Ajustalo a mano.`,
        variant: 'destructive',
      });
    } else {
      toast({ title: 'Columnas detectadas', description: `Nombre=${next.name} · IG=${next.instagram_url}${next.tiktok_url ? ` · TT=${next.tiktok_url}` : ''}${next.youtube_url ? ` · YT=${next.youtube_url}` : ''}${next.vertical ? ` · Vert=${next.vertical}` : ''}${next.pais_residencia ? ` · País=${next.pais_residencia}` : ''}${next.tipo_campana ? ` · Tipo=${next.tipo_campana}` : ''}` });
    }
    setMapping(next);
    setPreview(null);
  };

  const persistMapping = (m) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(m)); } catch (_e) { /* localStorage unavailable */ }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setSheets([]);
    setSheetName('');
    setDefaults({ vertical: '', pais_residencia: '', tipo_campana: '' });
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = String(reader.result || '');
      const b64 = dataUrl.includes(',') ? dataUrl.split(',', 2)[1] : dataUrl;
      setFileB64(b64);
      setPreview(null);
      setResult(null);
      // Cargar la lista de hojas del Excel
      try {
        const res = await fetch('/api/talentos/import-excel/sheets', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_base64: b64 }),
        });
        const data = await res.json();
        if (res.ok && Array.isArray(data.sheets)) {
          setSheets(data.sheets);
          const active = data.active || data.sheets[0] || '';
          setSheetName(active);
          suggestDefaultsFromSheet(active);
          if (active) await fetchHeaders(b64, active, startRow);
        }
      } catch (_e) { /* opcional: si falla, el input queda vacío y usa la hoja activa */ }
    };
    reader.readAsDataURL(file);
  };

  // Sugerir un default de tipo_campana a partir del nombre de la hoja.
  // Ej: "UGC CREATORS" → "UGC". Sólo sugiere si el user no seteó ya un default a mano.
  // Palabras conocidas que suelen aparecer en nombres de hoja dedicadas.
  const suggestDefaultsFromSheet = (sn) => {
    if (!sn) return;
    const up = String(sn).toUpperCase();
    const known = ['UGC', 'PACK', 'INFLUENCERS', 'INFLUENCER', 'CREATORS', 'TALENGERS'];
    const found = known.find((w) => new RegExp(`(?:^|[^A-Z0-9])${w}(?:[^A-Z0-9]|$)`).test(up));
    if (!found) return;
    setDefaults((prev) => (prev.tipo_campana ? prev : { ...prev, tipo_campana: found === 'UGC' ? 'UGC' : found }));
  };

  const buildColumnsPayload = () => {
    // Descarta mapeos vacíos (columnas opcionales que el user no configuró)
    const out = {};
    Object.entries(mapping).forEach(([k, v]) => {
      if (v && String(v).trim()) out[k] = String(v).trim().toUpperCase();
    });
    return out;
  };

  const runDryRun = async () => {
    if (!fileB64) {
      toast({ title: 'Falta el archivo', variant: 'destructive' });
      return;
    }
    setBusy(true); setResult(null);
    try {
      const res = await fetch('/api/talentos/import-excel', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_base64: fileB64, start_row: startRow, sheet_name: sheetName || null, columns: buildColumnsPayload(), defaults, dry_run: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error en preview');
      setPreview(data);
      persistMapping(mapping);
    } catch (e) {
      toast({ title: 'Error en preview', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const runImport = async () => {
    if (!fileB64) {
      toast({ title: 'Falta el archivo', variant: 'destructive' });
      return;
    }
    if (!window.confirm('¿Confirmás la importación? Los talentos nuevos se crearán como ocultos; los que ya existan se actualizarán con los campos que traigan datos nuevos.')) return;
    setBusy(true);
    try {
      const res = await fetch('/api/talentos/import-excel', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_base64: fileB64, start_row: startRow, sheet_name: sheetName || null, columns: buildColumnsPayload(), defaults, dry_run: false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error al importar');
      setResult(data);
      persistMapping(mapping);
      toast({ title: 'Importación completada', description: `${data.created || 0} creados · ${data.updated || 0} actualizados · ${data.unchanged || 0} sin cambios · ${data.errors?.length || 0} errores` });
      if (((data.created || 0) + (data.updated || 0)) > 0 && onImported) onImported();
    } catch (e) {
      toast({ title: 'Error al importar', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const resetAll = () => {
    setFileB64(''); setFileName(''); setPreview(null); setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const closeAndReset = () => { setOpen(false); resetAll(); };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
        data-testid="import-excel-talents-button"
      >
        <Upload className="w-4 h-4 mr-2" />
        Importar Talentos (Excel)
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) closeAndReset(); else setOpen(true); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="import-excel-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Importar Talentos desde Excel</DialogTitle>
            <p className="text-xs text-gray-400 mt-1">
              Talentos <strong className="text-emerald-400">nuevos</strong> se crean como <strong className="text-orange-400">🔒 ocultos</strong>.
              Los <strong className="text-cyan-400">existentes</strong> (match por handle IG) se <strong>actualizan</strong> con los campos que traigan datos nuevos.
            </p>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            {/* Step 1: File */}
            <div>
              <Label className="text-sm text-gray-300 uppercase tracking-wider">1. Archivo .xlsx</Label>
              <div className="mt-2 flex items-center gap-3">
                <input ref={fileRef} type="file" accept=".xlsx" onChange={onPickFile} className="hidden" data-testid="import-excel-file-input" />
                <Button type="button" onClick={() => fileRef.current?.click()} variant="outline" className="bg-gray-800 border-gray-700 text-white" data-testid="import-excel-choose-file">
                  <Upload className="w-4 h-4 mr-2" />Elegir archivo
                </Button>
                <span className="text-sm text-gray-400 truncate" data-testid="import-excel-filename">{fileName || 'Ningún archivo seleccionado'}</span>
              </div>
            </div>

            {/* Step 1b: Sheet picker (aparece cuando el file tiene varias hojas) */}
            {sheets.length > 0 && (
              <div>
                <Label className="text-sm text-gray-300 uppercase tracking-wider">1b. Hoja del Excel</Label>
                <Select value={sheetName} onValueChange={async (v) => { setSheetName(v); setPreview(null); suggestDefaultsFromSheet(v); if (fileB64) await fetchHeaders(fileB64, v, startRow); }}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 mt-2" data-testid="import-excel-sheet-select">
                    <SelectValue placeholder="Elegí una hoja" />
                  </SelectTrigger>
                  <SelectContent>
                    {sheets.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-xs text-gray-500 mt-1">{sheets.length} hoja{sheets.length !== 1 ? 's' : ''} disponibles</div>
              </div>
            )}

            {/* Step 2: Config */}
            <div>
              <Label className="text-sm text-gray-300 uppercase tracking-wider">2. Configuración</Label>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Fila donde empiezan los datos</Label>
                  <Input type="number" min={1} value={startRow} onChange={(e) => {
                    const v = parseInt(e.target.value || '1', 10);
                    setStartRow(v);
                    if (fileB64) fetchHeaders(fileB64, sheetName, v);
                  }} className="bg-gray-800 border-gray-700 mt-1" data-testid="import-excel-start-row" />
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                {IMPORT_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs text-gray-500">{f.label}{f.required && <span className="text-red-400"> *</span>}</Label>
                    <Input
                      value={mapping[f.key] || ''}
                      onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value.toUpperCase() })}
                      placeholder="A"
                      maxLength={3}
                      className="bg-gray-800 border-gray-700 mt-1 uppercase"
                      data-testid={`import-excel-map-${f.key}`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <button type="button" onClick={autoDetect} disabled={headerRows.length === 0} className="text-xs bg-cyan-600 hover:bg-cyan-500 disabled:bg-neutral-800 disabled:text-gray-500 text-white font-semibold px-3 py-1.5 rounded" data-testid="import-excel-autodetect">
                  ✨ Detectar columnas automáticamente
                </button>
                <button type="button" onClick={() => setMapping(DEFAULT_MAPPING)} className="text-xs text-cyan-400 hover:underline" data-testid="import-excel-reset-mapping">
                  Restaurar valores por defecto (A, B, C, G, H, N, O)
                </button>
              </div>

              {/* Defaults por hoja: se aplican a las filas SIN valor propio en su columna. */}
              <div className="mt-4 rounded-lg border border-violet-800/40 bg-violet-950/10 p-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-violet-300 uppercase tracking-wider font-semibold">
                    Defaults por hoja <span className="text-gray-500 normal-case font-normal">(opcional)</span>
                  </Label>
                  {sheetName && (
                    <span className="text-[10px] text-gray-500">
                      Se aplican a filas sin valor propio en su columna
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  Usalo cuando la hoja no tiene una columna para ese campo pero TODAS las filas
                  comparten el valor. Ej: hoja <strong>UGC CREATORS</strong> → Tipo Campaña por defecto:{' '}
                  <span className="text-violet-300 font-mono">UGC</span>. Nunca sobrescribe un valor que
                  ya vino en la fila del Excel.
                </p>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-gray-400">Tipo Campaña por defecto</Label>
                    <Input
                      value={defaults.tipo_campana}
                      onChange={(e) => setDefaults({ ...defaults, tipo_campana: e.target.value })}
                      placeholder="Ej: UGC"
                      className="bg-gray-800 border-gray-700 mt-1"
                      data-testid="import-excel-default-tipo-campana"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">Vertical por defecto</Label>
                    <Input
                      value={defaults.vertical}
                      onChange={(e) => setDefaults({ ...defaults, vertical: e.target.value })}
                      placeholder="Ej: Lifestyle, Beauty"
                      className="bg-gray-800 border-gray-700 mt-1"
                      data-testid="import-excel-default-vertical"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">País Residencia por defecto</Label>
                    <Input
                      value={defaults.pais_residencia}
                      onChange={(e) => setDefaults({ ...defaults, pais_residencia: e.target.value })}
                      placeholder="Ej: Argentina"
                      className="bg-gray-800 border-gray-700 mt-1"
                      data-testid="import-excel-default-pais"
                    />
                  </div>
                </div>
              </div>
              {/* Header preview: muestra qué hay en cada columna de las primeras 2 filas */}
              {headerRows.length > 0 && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-gray-400 hover:text-gray-300">Ver headers de la hoja &quot;{sheetName}&quot; (para ubicar columnas)</summary>
                  <div className="mt-2 border border-neutral-800 rounded overflow-x-auto">
                    <table className="min-w-full text-[10px] font-mono">
                      <thead>
                        <tr className="bg-neutral-900">
                          {Array.from({ length: Math.min(headerRows[0]?.length || 0, 26) }, (_, c) => (
                            <th key={c} className="px-2 py-1 border-r border-neutral-800 text-cyan-400">{idxToLetter(c)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {headerRows.map((row, ri) => (
                          <tr key={ri} className={ri % 2 ? 'bg-neutral-950' : ''}>
                            {Array.from({ length: Math.min(row.length, 26) }, (_, c) => (
                              <td key={c} className="px-2 py-1 border-r border-neutral-800 max-w-[120px] truncate text-gray-300" title={row[c]}>{row[c] || '—'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
            </div>

            {/* Step 3: Preview */}
            <div>
              <div className="flex items-center gap-2">
                <Button type="button" onClick={runDryRun} disabled={busy || !fileB64} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="import-excel-preview">
                  {busy ? 'Procesando...' : 'Vista previa'}
                </Button>
                <Button type="button" onClick={runImport} disabled={busy || !fileB64} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="import-excel-import">
                  {busy ? 'Importando...' : 'Importar / Actualizar'}
                </Button>
              </div>

              {preview && !result && (
                <div className="mt-4 rounded-lg border border-gray-700 bg-gray-950 p-3 text-xs" data-testid="import-excel-preview-result">
                  <div className="text-gray-300 mb-2">
                    Hoja: <strong>{preview.sheet_used}</strong> ·
                    <span className="text-emerald-400"> {preview.preview?.filter(p => p.action === 'create').length || 0} crear</span> ·
                    <span className="text-cyan-400"> {preview.preview?.filter(p => p.action === 'update').length || 0} actualizar</span> ·
                    <span className="text-gray-400"> {preview.preview?.filter(p => p.action === 'unchanged').length || 0} sin cambios</span> ·
                    <span className="text-red-400"> {preview.errors?.length || 0} errores</span>
                  </div>
                  {(preview.preview || []).slice(0, 10).map((p, i) => (
                    <div key={i} className="border-t border-gray-800 pt-2 mt-2 grid grid-cols-5 gap-2">
                      <div>
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase mr-2 ${
                          p.action === 'create' ? 'bg-emerald-500/20 text-emerald-300' :
                          p.action === 'update' ? 'bg-cyan-500/20 text-cyan-300' :
                          'bg-gray-700/40 text-gray-400'
                        }`}>{p.action || '—'}</span>
                        <span className="text-gray-500">F{p.row}:</span> <strong>{p.name || <em className="text-gray-500">(sin nombre)</em>}</strong>
                      </div>
                      <div className="truncate"><span className="text-gray-500">IG:</span> {p.instagram_followers || '—'}</div>
                      <div className="truncate"><span className="text-gray-500">TT:</span> {p.tiktok_followers || '—'}</div>
                      <div className="truncate"><span className="text-gray-500">Vertical:</span> <span className="text-orange-300">{p.vertical || '—'}</span></div>
                      <div className="truncate"><span className="text-gray-500">País:</span> <span className="text-pink-300">{p.pais_residencia || '—'}</span> · <span className="text-gray-500">Tipo:</span> <span className="text-violet-300">{p.tipo_campana || '—'}</span></div>
                      {p.action === 'update' && p.diff && (
                        <div className="col-span-5 text-cyan-300 text-[11px]">
                          Cambios: {Object.entries(p.diff).map(([k, v]) => `${k}=${v}`).join(' · ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {result && (
                <div className="mt-4 rounded-lg border border-emerald-700/60 bg-emerald-900/20 p-3 text-sm" data-testid="import-excel-final-result">
                  <div className="text-emerald-300 font-semibold text-base">
                    ✅ Importación completada
                  </div>
                  <div className="text-gray-300 mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div><span className="text-emerald-400 font-semibold">{result.created}</span> creados (ocultos)</div>
                    <div><span className="text-cyan-400 font-semibold">{result.updated}</span> actualizados</div>
                    <div><span className="text-gray-400 font-semibold">{result.unchanged}</span> sin cambios</div>
                    <div><span className="text-red-400 font-semibold">{result.errors?.length || 0}</span> errores</div>
                  </div>
                  {(result.errors || []).length > 0 && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-red-300">Ver errores ({result.errors.length})</summary>
                      <div className="mt-2 max-h-40 overflow-y-auto">
                        {result.errors.map((e, i) => (
                          <div key={i} className="text-red-400">Fila {e.row}: {e.error}</div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};


// Backfill Tipo Campaña — arregla batchs importados antes de existir "Defaults por hoja".
// Aplica un valor (ej: "UGC") a TODOS los talentos ocultos que hoy tengan tipo_campana vacío.
// Nunca sobreescribe talentos que ya tienen un valor.
const BackfillTipoCampanaButton = ({ toast, onDone }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('UGC');
  const [onlyHidden, setOnlyHidden] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { matched, value, only_hidden }

  const runDryRun = async () => {
    setBusy(true); setPreview(null);
    try {
      const res = await fetch('/api/talentos/backfill-tipo-campana', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value, only_hidden: onlyHidden, dry_run: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      setPreview(data);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const runApply = async () => {
    if (!preview || preview.matched === 0) return;
    if (!window.confirm(`¿Confirmás aplicar tipo_campana="${value}" a ${preview.matched} talentos${onlyHidden ? ' ocultos' : ''}? Esta acción NO se puede deshacer con un click.`)) return;
    setBusy(true);
    try {
      const res = await fetch('/api/talentos/backfill-tipo-campana', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value, only_hidden: onlyHidden, dry_run: false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      toast({ title: 'Backfill completado', description: `${data.updated} talentos actualizados con tipo_campana="${data.value}"` });
      setOpen(false);
      setPreview(null);
      if (onDone) onDone();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        onClick={() => { setOpen(true); setPreview(null); }}
        variant="outline"
        className="border-violet-500 text-violet-400 hover:bg-violet-500 hover:text-white"
        data-testid="backfill-tipo-campana-button"
      >
        Backfill Tipo Campaña
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setPreview(null); } }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg" data-testid="backfill-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">Backfill Tipo Campaña</DialogTitle>
            <p className="text-xs text-gray-400 mt-2 leading-relaxed">
              Aplica un valor de <strong className="text-violet-300">Tipo Campaña</strong> a todos los talentos
              que actualmente NO tengan ese campo cargado. Sirve para arreglar batchs importados sin este dato.
              <br /><strong className="text-emerald-400">Nunca sobreescribe</strong> a talentos que ya tienen un valor.
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-3">
            <div>
              <Label className="text-xs text-gray-400">Valor a aplicar</Label>
              <Input
                value={value}
                onChange={(e) => { setValue(e.target.value); setPreview(null); }}
                placeholder="UGC"
                className="bg-gray-800 border-gray-700 mt-1"
                data-testid="backfill-value-input"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyHidden}
                onChange={(e) => { setOnlyHidden(e.target.checked); setPreview(null); }}
                data-testid="backfill-only-hidden"
              />
              Sólo talentos ocultos <span className="text-gray-500 text-xs">(recomendado — los públicos suelen ya tener sus datos completos)</span>
            </label>

            <div className="flex gap-2">
              <Button
                type="button"
                onClick={runDryRun}
                disabled={busy || !value.trim()}
                variant="outline"
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="backfill-count-button"
              >
                {busy ? 'Contando...' : '1. Contar cuántos'}
              </Button>
              <Button
                type="button"
                onClick={runApply}
                disabled={busy || !preview || preview.matched === 0}
                className="bg-violet-500 hover:bg-violet-600 text-white"
                data-testid="backfill-apply-button"
              >
                {busy ? 'Aplicando...' : `2. Aplicar${preview ? ` (${preview.matched})` : ''}`}
              </Button>
            </div>

            {preview && (
              <div className="rounded-lg border border-violet-700/50 bg-violet-950/20 p-3 text-sm" data-testid="backfill-preview">
                {preview.matched === 0 ? (
                  <p className="text-gray-300">
                    No hay talentos {onlyHidden ? 'ocultos ' : ''}con <span className="font-mono text-gray-400">tipo_campana</span> vacío.
                    Nada para actualizar.
                  </p>
                ) : (
                  <p className="text-gray-200">
                    Se van a actualizar <strong className="text-violet-300">{preview.matched}</strong> talentos
                    {onlyHidden ? ' ocultos' : ''} → <span className="font-mono text-violet-300">tipo_campana = &quot;{preview.value}&quot;</span>
                  </p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};


// Apply Verticals Button — accepts an Excel where headers are vertical names and
// cells underneath are talent names. Matches by surname against current roster.
const ApplyVerticalsButton = ({ toast, onApplied }) => {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  const upload = async (file, dryRun) => {
    setRunning(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/talentos/apply-verticals?dry_run=${dryRun}`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (dryRun) setReport({ ...data, _file: file });
      else {
        toast({ title: 'Verticales aplicados', description: `${data.applied} talentos actualizados.` });
        setReport(null);
        onApplied?.();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo procesar', variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) await upload(file, true);
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFile}
        className="hidden"
      />
      <Button
        onClick={() => fileRef.current?.click()}
        disabled={running}
        variant="outline"
        className="border-cyan-500 text-cyan-400 hover:bg-cyan-500 hover:text-white"
        data-testid="apply-verticals-button"
      >
        {running ? 'Procesando...' : 'Aplicar Verticales (Excel)'}
      </Button>

      {report && (
        <Dialog open={!!report} onOpenChange={(o) => !o && setReport(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Previsualización · {report.matched_count} talentos a actualizar
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <p className="text-gray-400">
                Revisá los matches y confirmá. Esto sobrescribe el campo <span className="font-mono text-cyan-400">vertical</span> de cada talento listado.
              </p>
              <div className="border border-gray-800 rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="text-left py-2 px-3">Excel</th>
                      <th className="text-left py-2 px-3">Roster</th>
                      <th className="text-left py-2 px-3">Verticales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.matched.map((m, i) => (
                      <tr key={i} className="border-t border-gray-800">
                        <td className="py-1.5 px-3 text-gray-400">{m.excel_name}</td>
                        <td className="py-1.5 px-3 text-white font-semibold">{m.roster_name}</td>
                        <td className="py-1.5 px-3 text-cyan-300">{m.verticals.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {report.unmatched_excel?.length > 0 && (
                <details className="border border-gray-800 rounded p-3 bg-gray-950">
                  <summary className="cursor-pointer text-yellow-400 font-semibold">
                    {report.unmatched_excel.length} nombres del Excel sin coincidencia (se descartan)
                  </summary>
                  <p className="mt-2 text-gray-400 text-xs">{report.unmatched_excel.join(', ')}</p>
                </details>
              )}
              {report.untouched_roster?.length > 0 && (
                <details className="border border-gray-800 rounded p-3 bg-gray-950">
                  <summary className="cursor-pointer text-gray-400 font-semibold">
                    {report.untouched_roster.length} talentos del roster sin datos en Excel (no se tocan)
                  </summary>
                  <p className="mt-2 text-gray-500 text-xs">{report.untouched_roster.join(', ')}</p>
                </details>
              )}
              <div className="flex gap-2 justify-end pt-3 border-t border-gray-800">
                <Button variant="outline" onClick={() => setReport(null)} className="border-gray-700">
                  Cancelar
                </Button>
                <Button
                  onClick={() => upload(report._file, false)}
                  disabled={running || !report._file}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  data-testid="apply-verticals-confirm"
                >
                  {running ? 'Aplicando...' : `Aplicar a ${report.matched_count} talentos`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};


// Normalize Verticals — button that reviews every talento and rewrites the
// `vertical` field applying canonical aliases (Foodies→Foodie, Beauty/Make Up/
// Makeup→Beauty & Make Up, Deporte→Sport, Dance→Baile) and unifying casing.
const NormalizeVerticalsButton = ({ toast, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { total, updated, unchanged, changes }

  const call = async (dryRun) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/talentos/normalize-verticals?dry_run=${dryRun}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      return data;
    } finally {
      setBusy(false);
    }
  };

  const runDryRun = async () => {
    setPreview(null);
    try {
      const data = await call(true);
      setPreview(data);
      setOpen(true);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const runApply = async () => {
    if (!preview) return;
    try {
      const data = await call(false);
      toast({
        title: 'Verticales normalizadas',
        description: `${data.updated} talentos actualizados · ${data.unchanged} ya estaban OK`,
      });
      setOpen(false);
      setPreview(null);
      onDone?.();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <>
      <Button
        onClick={runDryRun}
        disabled={busy}
        variant="outline"
        className="border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-white"
        data-testid="normalize-verticals-button"
      >
        {busy && !open ? 'Analizando...' : 'Normalizar Verticales'}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setPreview(null); } }}>
        <DialogContent
          className="bg-gray-900 border-gray-800 text-white max-w-2xl max-h-[80vh] overflow-y-auto"
          data-testid="normalize-verticals-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-xl">Normalizar Verticales</DialogTitle>
            <p className="text-xs text-gray-400 mt-2 leading-relaxed">
              Aplica el mapeo canónico a todos los talentos:
              <br />
              <span className="text-emerald-300">Foodies → Foodie</span> ·{' '}
              <span className="text-emerald-300">Deporte → Sport</span> ·{' '}
              <span className="text-emerald-300">Beauty / Make Up / Makeup → Beauty &amp; Make Up</span> ·{' '}
              <span className="text-emerald-300">Dance → Baile</span>
              <br />
              También unifica mayúsculas/minúsculas y espacios en todas las verticales.
            </p>
          </DialogHeader>

          {preview && (
            <div className="space-y-4 mt-3 text-sm">
              <div className="flex gap-4 text-xs">
                <div className="text-gray-400">Total talentos: <span className="text-white font-semibold">{preview.total}</span></div>
                <div className="text-emerald-400">A actualizar: <span className="font-semibold">{preview.updated}</span></div>
                <div className="text-gray-500">Ya OK: <span className="font-semibold">{preview.unchanged}</span></div>
              </div>

              {preview.changes && preview.changes.length > 0 ? (
                <div className="border border-gray-800 rounded overflow-hidden max-h-[45vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-800 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3">Talento</th>
                        <th className="text-left py-2 px-3">Antes</th>
                        <th className="text-left py-2 px-3">Después</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.changes.map((c, i) => (
                        <tr key={i} className="border-t border-gray-800">
                          <td className="py-1.5 px-3 text-white font-semibold whitespace-nowrap">{c.name}</td>
                          <td className="py-1.5 px-3 text-gray-400">{c.before || '—'}</td>
                          <td className="py-1.5 px-3 text-emerald-300">{c.after || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400 text-sm">
                  Nada para normalizar. Todos los talentos ya usan la forma canónica.
                </div>
              )}

              <div className="flex gap-2 justify-end pt-3 border-t border-gray-800">
                <Button variant="outline" onClick={() => setOpen(false)} className="border-gray-700" data-testid="normalize-verticals-cancel">
                  Cancelar
                </Button>
                <Button
                  onClick={runApply}
                  disabled={busy || preview.updated === 0}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="normalize-verticals-confirm"
                >
                  {busy ? 'Aplicando...' : `Aplicar a ${preview.updated} talento${preview.updated === 1 ? '' : 's'}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};







export default AdminPanel;
